/* cms look — desktop in-card panel + mobile bottom sheet (#look-modal)
   ========================================================================== */
import Dialogs from '../../dialogs/a11y-dialog-component';
import addLazyLoad from '../../lazyLoad/index';
import addState from '../../states/index';
import addVideos from '../../videos/index';
import { desktopMediaQueryListener, isDesktopDisplay } from '../../mobile/check';
const LOOK_MODAL_ID = 'look-modal';
let activeLook = null;
let lookModalInitialContent = '';
let cancelPendingRestoreLookModal = null;
function clonePopin(card) {
    const template = card.querySelector('[data-look-panel-template]');
    const source = template?.content.querySelector('[data-look-popin]');
    return source ? source.cloneNode(true) : null;
}
function getLookModalContent() {
    return document.querySelector(`#${LOOK_MODAL_ID} [data-modal-content]`);
}
function initMedia(root) {
    addLazyLoad();
    addVideos(root);
    addState(root);
}
function restoreLookModal() {
    const container = getLookModalContent();
    if (container) {
        container.innerHTML = lookModalInitialContent;
    }
}
function restoreLookModalAfterClose() {
    cancelPendingRestoreLookModal?.();
    const modal = document.getElementById(LOOK_MODAL_ID);
    const inner = modal?.querySelector('.c-dialog__inner');
    if (!inner || !modal) {
        restoreLookModal();
        return;
    }
    const onTransitionEnd = (event) => {
        if (event.target !== inner || event.propertyName !== 'transform') {
            return;
        }
        cancelPendingRestoreLookModal?.();
        if (modal.getAttribute('aria-hidden') !== 'false') {
            restoreLookModal();
        }
    };
    inner.addEventListener('transitionend', onTransitionEnd);
    cancelPendingRestoreLookModal = () => {
        inner.removeEventListener('transitionend', onTransitionEnd);
        cancelPendingRestoreLookModal = null;
    };
}
function syncMobileLookClosed(restoreFocus = false) {
    if (activeLook?.mode !== 'mobile') {
        return;
    }
    const { open } = activeLook;
    activeLook = null;
    open.setAttribute('aria-expanded', 'false');
    if (restoreFocus) {
        open.focus();
    }
}
function initLookModal() {
    const container = getLookModalContent();
    if (!container || !document.getElementById(LOOK_MODAL_ID)) {
        return;
    }
    lookModalInitialContent = container.innerHTML;
    Dialogs.create(LOOK_MODAL_ID, {
        // Match c-dialog bottom-sheet duration (--transition-duration: 0.8s) so setFocus runs after the slide-in
        transitionDuration: 800,
        onClose: () => {
            // Backdrop / dialog dismiss has no trigger — restore focus on the look opener
            syncMobileLookClosed(true);
            restoreLookModalAfterClose();
        },
    });
}
function revealPanel(popin, mode) {
    if (mode === 'desktop') {
        popin.setAttribute('aria-hidden', 'true');
    }
    // Double requestAnimationFrame to let the browser paint before opening (fix jump Safari mobile)
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            if (activeLook?.popin !== popin) {
                return;
            }
            popin.setAttribute('aria-hidden', 'false');
            if (mode === 'mobile') {
                Dialogs.show(LOOK_MODAL_ID);
            }
            initMedia(popin);
        });
    });
}
function removeDesktopPanel(popin, animated, onRemoved) {
    const cleanup = () => {
        if (!popin.isConnected) {
            return;
        }
        popin.remove();
        onRemoved?.();
    };
    if (!animated) {
        cleanup();
        return;
    }
    const onTransitionEnd = (event) => {
        if (event.target !== popin
            || (event.propertyName !== 'translate' && event.propertyName !== 'transform')) {
            return;
        }
        popin.removeEventListener('transitionend', onTransitionEnd);
        cleanup();
    };
    popin.addEventListener('transitionend', onTransitionEnd);
    requestAnimationFrame(() => {
        if (!popin.isConnected) {
            return;
        }
        popin.setAttribute('aria-hidden', 'true');
    });
}
function closeLook(restoreFocus = true, animated = true) {
    if (!activeLook) {
        return;
    }
    const { open, popin, mode } = activeLook;
    if (mode === 'desktop') {
        activeLook = null;
        open.setAttribute('aria-expanded', 'false');
        removeDesktopPanel(popin, animated, restoreFocus ? () => open.focus() : undefined);
        return;
    }
    syncMobileLookClosed(restoreFocus);
    Dialogs.hide(LOOK_MODAL_ID);
}
function openLook(open) {
    const card = open.closest('.card-look');
    if (!card) {
        return;
    }
    if (activeLook?.card === card) {
        return;
    }
    closeLook(false, true);
    const popin = clonePopin(card);
    if (!popin) {
        return;
    }
    const mode = isDesktopDisplay() ? 'desktop' : 'mobile';
    if (mode === 'desktop') {
        card.appendChild(popin);
    }
    else {
        cancelPendingRestoreLookModal?.();
        const container = getLookModalContent();
        if (!container) {
            return;
        }
        container.replaceChildren(popin);
    }
    activeLook = { card, open, popin, mode };
    open.setAttribute('aria-expanded', 'true');
    revealPanel(popin, mode);
}
export function initLookPanels() {
    if (!document.querySelector('.card-look [data-look-panel-template]')) {
        return;
    }
    initLookModal();
    desktopMediaQueryListener.addEventListener('change', () => {
        if (activeLook) {
            closeLook(false, false);
        }
    });
    document.addEventListener('click', (event) => {
        const open = event.target.closest('[data-look-open]');
        if (open) {
            event.preventDefault();
            openLook(open);
            return;
        }
        const close = event.target.closest('[data-look-close]');
        if (close && activeLook?.popin.contains(close)) {
            event.preventDefault();
            closeLook();
        }
    });
    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !activeLook) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        closeLook();
    }, { capture: true });
}
